// src/oura/tasks/model.ts
//
// Tasks: the client's Master Tracker in Oura (Jack, 23 Oct 2026). One task per
// row of the Google Sheet Angel works in, grouped by phase (P0 access and
// baseline, then M1 to M6). The sheet and Oura hold the same rows; each row
// carries an Oura ID so either side can change it.

export type TaskStatus = "Not started" | "In progress" | "Blocked" | "Awaiting client" | "Done";

export const TASK_STATUSES: TaskStatus[] = ["Not started", "In progress", "Blocked", "Awaiting client", "Done"];

export interface TaskHistory {
    field: string;
    before: string | null;
    after: string | null;
    where: "sheet" | "oura";
    by: string | null;
    at: string | null;
    note: string | null;
}

export interface Task {
    id: string;
    /** The sheet's Priority column: the order inside the phase. */
    n: number | null;
    action: string;
    notes: string;
    tool: string;
    /** What Done means for this row. */
    gate: string;
    /** "ourteam", "Client" or "ourteam + Client". */
    owner: string;
    /** "29 Oct 2026". */
    target: string;
    status: string;
    /** The evidence, dated. */
    result: string;
    /** Live tasks only: the plan codes the action starts with, a free label, and what changed. */
    codes?: string[];
    label?: string | null;
    history?: TaskHistory[];
    changedInSheet?: { by: string | null; at: string | null } | null;
}

export interface TaskPhase {
    /** "P0", "M1". */
    key: string;
    /** The sheet tab's name: "M2 KL and PJ". */
    name: string;
    title: string;
    sub: string;
    tasks: Task[];
}

/**
 * The codes an action starts with, spaced ("G1 G5 Publish") or joined by
 * hyphens the way doc names write them ("G2-G3-G4-G7 Monthly report", #4814).
 * "G1-KL x" is a code and an outlet, not a code list. The backend reads the
 * sheet with the same rule (#6688).
 */
const LEADING_CODES = /^(?:[A-Z]\d{1,2}(?:-[A-Z]\d{1,2})*\s+)+/;

/** The plan lines a task belongs to: every code its action starts with, each once ("G2-G3 Publish..." is G2 and G3). */
export function codesOf(action: string): string[] {
    const m = LEADING_CODES.exec(action);
    return m ? Array.from(new Set(m[0].trim().split(/[\s-]+/))) : [];
}

/** The action without its leading codes. */
export function actionText(action: string): string {
    return action.replace(LEADING_CODES, "");
}

export function ownerKind(owner: string): "us" | "client" | "both" {
    const o = owner.toLowerCase();
    if (o.includes("client") && o.includes("ourteam")) return "both";
    return o.includes("client") ? "client" : "us";
}

/** Done of total, per phase. */
export function phaseCount(p: TaskPhase): { done: number; total: number } {
    return {
        done: p.tasks.filter((t) => t.status === "Done").length,
        total: p.tasks.length,
    };
}

/**
 * Each plan line in a phase, with how many of its tasks are done. A plan line
 * is done when all its tasks are. A task with several codes counts under each
 * (as Sheet decides Done counts it); rows with no code sit under "General".
 */
export function planLines(p: TaskPhase): { code: string; done: number; total: number }[] {
    const by = new Map<string, { done: number; total: number }>();
    for (const t of p.tasks) {
        const codes = codesOf(t.action);
        for (const code of codes.length ? codes : ["General"]) {
            const c = by.get(code) ?? { done: 0, total: 0 };
            c.total += 1;
            if (t.status === "Done") c.done += 1;
            by.set(code, c);
        }
    }
    return Array.from(by.entries())
        .map(([code, c]) => ({ code, ...c }))
        .sort((a, b) => (a.code === "General" ? 1 : b.code === "General" ? -1 : a.code.localeCompare(b.code)));
}

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-11-26" as the sheet writes it, "26 Nov 2026". */
export function sheetDay(iso: string | null): string {
    const m = iso ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso) : null;
    return m ? `${Number(m[3])} ${MON[Number(m[2]) - 1]} ${m[1]}` : "";
}

/** "26 Nov 2026" back to "2026-11-26" for a date input; null when the text is not a date (free text in the sheet). */
export function isoDay(text: string | null): string | null {
    const m = text ? /^(\d{1,2}) ([A-Z][a-z]{2}) (\d{4})$/.exec(text.trim()) : null;
    const month = m ? MON.indexOf(m[2]) : -1;
    return m && month >= 0 ? `${m[3]}-${String(month + 1).padStart(2, "0")}-${m[1].padStart(2, "0")}` : null;
}

/** A task from GET /businesses/{id}/tasks, in the shape the view draws. */
export function taskFromApi(t: {
    id: string; order: number; action: string; codes: string[]; label: string | null; notes: string; tool: string; gate: string;
    owner: string; target: string | null; targetText: string | null; status: string; result: string;
    changedInSheet: { by: string | null; at: string | null } | null; history: TaskHistory[];
}): Task {
    return {
        id: t.id,
        n: t.order || null,
        action: t.action,
        notes: t.notes,
        tool: t.tool,
        gate: t.gate,
        owner: t.owner,
        target: t.target ? sheetDay(t.target) : (t.targetText ?? ""),
        status: t.status,
        result: t.result,
        codes: t.codes,
        label: t.label,
        history: t.history,
        changedInSheet: t.changedInSheet,
    };
}
